import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { exportRivalCv } from "@/lib/export";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Crosshair, CalendarDays, Star, Video, Link2,
  FileText, ClipboardList, Trophy,
} from "lucide-react";
import {
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip,
} from "recharts";
import { RIVAL_DECISIONS, RIVAL_EVAL_AREAS, RIVAL_SCALE } from "@/lib/rivalCategories";

const SCALE_BY_VALUE = Object.fromEntries(RIVAL_SCALE.map((s) => [s.value, s]));
const BAR_COLOR = { 4: "bg-emerald-500", 3: "bg-blue-500", 2: "bg-amber-500", 1: "bg-rose-500" };

const Kpi = ({ icon: Icon, label, value, sub, color = "text-purple-700", testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-slate-50 grid place-items-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-extrabold text-slate-900 leading-tight">{value}</div>
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
        {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

const Section = ({ title, icon: Icon, children, testId }) => (
  <Card className="border-slate-200" data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-purple-600" />} {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Empty = ({ text }) => <p className="text-xs text-slate-400 py-6 text-center">{text}</p>;

export default function RivalPlayerDashboard() {
  const { rivalId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/rival-players/${rivalId}/dashboard`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [rivalId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="rival-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  if (!data) return <Empty text="No se pudo cargar el jugador." />;

  const ev = data.evaluation || {};
  const scored = RIVAL_EVAL_AREAS
    .map((a) => ({ ...a, score: ev[a.key]?.score ?? null, comment: ev[a.key]?.comment || "" }))
    .filter((a) => a.score || a.comment);
  const withScore = scored.filter((a) => a.score);
  const avg = withScore.length
    ? (withScore.reduce((s, a) => s + a.score, 0) / withScore.length).toFixed(1)
    : null;
  const radarData = RIVAL_EVAL_AREAS.map((a) => ({
    area: a.label.replace("Aspectos ", "").replace("Aspecto ", ""),
    score: ev[a.key]?.score ?? 0,
  }));
  const decision = RIVAL_DECISIONS.find((d) => d.value === ev.decision);
  const age = data.birth_year ? new Date().getFullYear() - data.birth_year : null;

  return (
    <div className="space-y-4" data-testid="rival-player-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="rival-dashboard-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
        <div className="flex gap-2">
          {data.video_url && (
            <a href={data.video_url} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="rival-dashboard-video">
                <Video className="h-4 w-4 mr-2" /> Vídeo
              </Button>
            </a>
          )}
          {data.rffm_url && (
            <a href={data.rffm_url} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="rival-dashboard-rffm">
                <Link2 className="h-4 w-4 mr-2" /> Ficha RFFM
              </Button>
            </a>
          )}
          {data.cv_filename && (
            <Button variant="outline" onClick={() => exportRivalCv(data.id, data.cv_filename)} className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="rival-dashboard-cv">
              <FileText className="h-4 w-4 mr-2" /> CV
            </Button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-purple-800 to-purple-600 text-white p-6 flex flex-wrap items-center gap-6" data-testid="rival-dashboard-header">
        <div className="w-20 h-20 rounded-2xl bg-white/15 border border-white/25 grid place-items-center shrink-0">
          <Crosshair className="h-9 w-9" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight" data-testid="rival-dashboard-name">{data.name}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.position && <Badge className="bg-white/20 text-white border-0">{data.position}</Badge>}
            {data.rival_team && <Badge className="bg-white/20 text-white border-0">{data.rival_team}</Badge>}
            {data.category && (
              <Badge className="bg-white/10 text-purple-100 border-0">
                {data.category}{data.sub_category ? ` · ${data.sub_category}` : ""}
              </Badge>
            )}
            {decision && <Badge className={`border ${decision.cls}`}>{decision.label}</Badge>}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-purple-100">
            {data.birth_year && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" /> {data.birth_year}{age ? ` · ~${age} años` : ""}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <ClipboardList className="h-4 w-4" /> Seguimiento: {data.team_name}
            </span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={CalendarDays} label="Año nacimiento" value={data.birth_year || "—"} sub={age ? `~${age} años` : null} testId="rival-kpi-year" />
        <Kpi icon={Star} label="Media evaluación" value={avg ? `${avg}/4` : "—"} sub={withScore.length ? `${withScore.length} áreas valoradas` : "sin valorar"} color="text-amber-600" testId="rival-kpi-avg" />
        <Kpi icon={Trophy} label="Veces destacado" value={data.times_highlighted ?? (data.matches || []).length} sub="mejor jugador rival" color="text-emerald-600" testId="rival-kpi-highlighted" />
        <Kpi
          icon={Crosshair}
          label="Decisión"
          value={decision ? decision.label : "—"}
          color={ev.decision === "fichar" ? "text-emerald-600" : ev.decision === "descartar" ? "text-rose-600" : "text-amber-600"}
          testId="rival-kpi-decision"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Radar de evaluación" icon={Star} testId="rival-section-radar">
          {withScore.length >= 3 ? (
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid />
                <PolarAngleAxis dataKey="area" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 4]} tickCount={5} tick={{ fontSize: 9 }} />
                <Radar dataKey="score" stroke="#7E22CE" fill="#A855F7" fillOpacity={0.45} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          ) : <Empty text="Valora al menos 3 áreas para ver el radar." />}
        </Section>

        <Section title="Evaluación por áreas" icon={ClipboardList} testId="rival-section-areas">
          {scored.length ? (
            <div className="space-y-3">
              {RIVAL_EVAL_AREAS.map((a) => {
                const score = ev[a.key]?.score ?? null;
                const comment = ev[a.key]?.comment || "";
                const scale = score ? SCALE_BY_VALUE[score] : null;
                return (
                  <div key={a.key} data-testid={`rival-area-${a.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-slate-700">{a.label}</span>
                      {scale ? (
                        <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${scale.cls}`}>
                          {score}/4 · {scale.label}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">Sin valorar</span>
                      )}
                    </div>
                    <div className="h-2 mt-1 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${score ? BAR_COLOR[score] : ""}`}
                        style={{ width: `${((score || 0) / 4) * 100}%` }}
                      />
                    </div>
                    {comment && <p className="text-[11px] text-slate-500 mt-1">{comment}</p>}
                  </div>
                );
              })}
            </div>
          ) : <Empty text="Sin evaluación registrada todavía." />}
        </Section>
      </div>

      {(ev.valoracion_final || ev.observacion || data.notes) && (
        <Section title="Valoración y observaciones" icon={FileText} testId="rival-section-notes">
          <div className="space-y-3 text-sm text-slate-700">
            {ev.valoracion_final && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Valoración final</div>
                <p className="whitespace-pre-line">{ev.valoracion_final}</p>
              </div>
            )}
            {ev.observacion && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Observación</div>
                <p className="whitespace-pre-line">{ev.observacion}</p>
              </div>
            )}
            {data.notes && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Notas</div>
                <p className="whitespace-pre-line">{data.notes}</p>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Partidos en los que destacó" icon={Trophy} testId="rival-section-matches">
        {(data.matches || []).length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Nuestro equipo</th>
                  <th className="py-2 pr-3">Rival</th>
                  <th className="py-2">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {data.matches.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3">{m.date}</td>
                    <td className="py-2 pr-3">{m.our_team_name || "—"}</td>
                    <td className="py-2 pr-3">{m.rival || "—"}</td>
                    <td className="py-2 font-bold">{m.result || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty text="Todavía no ha sido señalado como mejor jugador rival en ningún partido." />}
      </Section>
    </div>
  );
}
